/*  JobRoleAnalytics.jsx  */
import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import Chart from "chart.js/auto";

// Layout & UI
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// MUI Date-Pickers
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { TextField, useTheme } from "@mui/material";

// Custom component
import JobWorkAnalysisCards from "./jobRoleCards";
import {
  jobWorkanalysis,
  maintenanceCost,
  jobDistribution,
  vehicleTotalCost,
} from "config/apis";
import { useMaterialUIController } from "context";

function JobRoleAnalytics() {
  /* ----------------------- DATE STATE ----------------------- */
  const [startDate, setStartDate] = useState(new Date("2025-11-03"));
  const [endDate, setEndDate] = useState(new Date("2025-11-08"));

  /* ----------------------- DATA STATE ----------------------- */
  const [analysisData, setAnalysisData] = useState(null);
  const [distributionData, setDistributionData] = useState(null);
  const [lineData, setLineData] = useState(null);
  const [barData, setBarData] = useState(null);

  /* ----------------------- LOADING / ERRORS ----------------------- */
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);
  const [barLoading, setBarLoading] = useState(false);

  const [error, setError] = useState("");
  const [chartError, setChartError] = useState("");
  const [lineError, setLineError] = useState("");
  const [barError, setBarError] = useState("");

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();

  /* ----------------------- CHART REFS ----------------------- */
  const pieRef = useRef(null);
  const lineRef = useRef(null);
  const barRef = useRef(null);
  const pieChartInstance = useRef(null);
  const lineChartInstance = useRef(null);
  const barChartInstance = useRef(null);

  /* ----------------------- HELPERS ----------------------- */
  const formatDate = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const fetchWithDates = useCallback(
    async (dates) => {
      const token = localStorage.getItem("token");
      const payload = {
        startDate: formatDate(dates.start),
        endDate: formatDate(dates.end),
      };
      const config = {
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
      };

      setLoading(true);
      setError("");
      try {
        const res = await axios.post(jobWorkanalysis, payload, config);
        setAnalysisData(res.data);
      } catch (err) {
        setError("Failed to fetch job work analysis data.");
      } finally {
        setLoading(false);
      }

      setChartLoading(true);
      setChartError("");
      try {
        const res = await axios.post(jobDistribution, payload, config);
        setDistributionData(res.data);
      } catch {
        setChartError("Failed to fetch job distribution data.");
      } finally {
        setChartLoading(false);
      }

      setLineLoading(true);
      setLineError("");
      try {
        const res = await axios.post(maintenanceCost, payload, config);
        setLineData(res.data);
      } catch {
        setLineError("Failed to fetch maintenance cost data.");
      } finally {
        setLineLoading(false);
      }

      setBarLoading(true);
      setBarError("");
      try {
        const res = await axios.post(vehicleTotalCost, payload, config);
        setBarData(res.data);
      } catch {
        setBarError("Failed to fetch vehicle total cost data.");
      } finally {
        setBarLoading(false);
      }
    },
    []
  );

  /* ----------------------- INITIAL LOAD ----------------------- */
  useEffect(() => {
    fetchWithDates({ start: startDate, end: endDate });
  }, [fetchWithDates]);

  /* ----------------------- APPLY HANDLER ----------------------- */
  const handleApply = () => {
    if (startDate && endDate && startDate <= endDate) {
      fetchWithDates({ start: startDate, end: endDate });
    }
  };

  /* ----------------------- COMMON CHART OPTIONS ----------------------- */
  const getTextColor = () => (darkMode ? "#E0E0E0" : "#111");
  const getGridColor = () => (darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)");

  const baseTitle = {
    font: { size: 14, weight: "bold" },
    color: getTextColor(),
  };

  const baseLegend = {
    labels: { color: getTextColor() },
  };

  /* ----------------------- CHART RENDERERS ----------------------- */
  // Pie
  useEffect(() => {
    if (!distributionData || !pieRef.current) return;
    const labels = Object.keys(distributionData);
    const values = Object.values(distributionData);
    if (pieChartInstance.current) pieChartInstance.current.destroy();

    pieChartInstance.current = new Chart(pieRef.current, {
      type: "pie",
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: [
              "rgba(54, 162, 235, 0.8)",
              "rgba(255, 99, 132, 0.8)",
              "rgba(255, 206, 86, 0.8)",
              "rgba(75, 192, 192, 0.8)",
              "rgba(153, 102, 255, 0.8)",
            ],
            borderColor: darkMode ? "#222" : "#fff",
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom", ...baseLegend },
          title: { display: true, text: "Job Type Distribution", ...baseTitle },
        },
      },
    });
  }, [distributionData, darkMode]);

  // Line
  useEffect(() => {
    if (!lineData || !lineRef.current) return;
    const labels = Object.keys(lineData);
    const values = Object.values(lineData);
    if (lineChartInstance.current) lineChartInstance.current.destroy();

    lineChartInstance.current = new Chart(lineRef.current, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Maintenance Cost (₹)",
            data: values,
            fill: false,
            borderColor: darkMode ? "#90CAF9" : "#1E88E5",
            tension: 0.3,
            pointBackgroundColor: darkMode ? "#BBDEFB" : "#1976D2",
            pointRadius: 5,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: baseLegend,
          title: { display: true, text: "Maintenance Cost Over Time", ...baseTitle },
        },
        scales: {
          x: { ticks: { color: getTextColor() }, grid: { color: getGridColor() } },
          y: {
            beginAtZero: true,
            ticks: { color: getTextColor(), callback: (v) => `₹${v}` },
            grid: { color: getGridColor() },
          },
        },
      },
    });
  }, [lineData, darkMode]);

  // Bar
  useEffect(() => {
    if (!barData || !barRef.current) return;
    const labels = Object.keys(barData);
    const values = Object.values(barData);
    if (barChartInstance.current) barChartInstance.current.destroy();

    barChartInstance.current = new Chart(barRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Total Cost (₹)",
            data: values,
            backgroundColor: darkMode
              ? "rgba(144, 202, 249, 0.8)"
              : "rgba(66, 133, 244, 0.8)",
            borderRadius: 8,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: baseLegend,
          title: { display: true, text: "Total Cost by Vehicle", ...baseTitle },
        },
        scales: {
          x: { ticks: { color: getTextColor() }, grid: { color: getGridColor() } },
          y: {
            beginAtZero: true,
            ticks: { color: getTextColor(), callback: (v) => `₹${v}` },
            grid: { color: getGridColor() },
          },
        },
      },
    });
  }, [barData, darkMode]);

  /* ----------------------- RENDER ----------------------- */
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} alignItems="center" mb={3}>
            <Grid item xs={12} sm={5} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(v) => setStartDate(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    InputLabelProps={{ style: { color: getTextColor() } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={5} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(v) => setEndDate(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    InputLabelProps={{ style: { color: getTextColor() } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={2} md={2}>
              <MDButton
                fullWidth
                variant="gradient"
                color="warning"
                size="small"
                onClick={handleApply}
                disabled={
                  loading || chartLoading || lineLoading || barLoading
                }
              >
                {(loading || chartLoading || lineLoading || barLoading) && (
                  <CircularProgress size={18} color="inherit" />
                )}{" "}
                APPLY
              </MDButton>
            </Grid>
          </Grid>
        </LocalizationProvider>

        {loading && <p>Loading cards...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}
        {!loading && !error && analysisData && (
          <JobWorkAnalysisCards data={analysisData} />
        )}

        <MDBox mt={5}>
          <h4
            style={{
              fontWeight: "600",
              marginBottom: "20px",
              color: getTextColor(),
            }}
          >
            Visualizations
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "20px",
            }}
          >
            {[pieRef, lineRef, barRef].map((ref, i) => {
              const loadingState =
                i === 0 ? chartLoading : i === 1 ? lineLoading : barLoading;
              const errorState =
                i === 0 ? chartError : i === 1 ? lineError : barError;

              return (
                <div
                  key={i}
                  style={{
                    backgroundColor: darkMode ? "#1A2035" : "#fff",
                    color: getTextColor(),
                    padding: "15px",
                    borderRadius: "12px",
                    boxShadow: darkMode
                      ? "0 2px 8px rgba(0,0,0,0.6)"
                      : "0 2px 10px rgba(0,0,0,0.1)",
                    minHeight: "320px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {loadingState ? (
                    <p>Loading chart...</p>
                  ) : errorState ? (
                    <p style={{ color: "red" }}>{errorState}</p>
                  ) : (
                    <div style={{ width: "100%", height: "250px" }}>
                      <canvas ref={ref} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default JobRoleAnalytics;